import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasSellerAccess } from '../utils/subscriptionUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import MultiImageUpload from '../components/MultiImageUpload';
import { toast } from 'sonner';
import { Upload, Scan, Check, AlertCircle, DollarSign, Sparkles, Images, Award, Hash, ShieldCheck, Star, Package, RefreshCw } from 'lucide-react';

export const SellPage = () => {
  const { api, user, isAuthenticated, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  
  // Track if we've checked subscription status
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  
  const [image, setImage] = useState(null);
  const [images, setImages] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Autocomplete state
  const [setSearch, setSetSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  // Basic form data
  const [formData, setFormData] = useState({
    player_name: '',
    sport: '',
    set_name: '',
    year: new Date().getFullYear(),
    grade_company: '',
    grade_value: '',
    price: '',
    description: ''
  });

  // Item Specifics (detailed card attributes)
  const [itemSpecifics, setItemSpecifics] = useState({
    brand_product: '',
    card_number: '',
    professional_grader: '',
    grade: '',
    cert_number: '',
    raw_condition: '',  // New field for raw cards
    is_autographed: false,
    signer_name: '',
    auto_authentication: '',
    is_serial_numbered: false,
    serial_number: '',
    total_print_run: '',
    parallel_variety: '',
    is_rookie_card: false,
    condition: 'Graded'
  });

  // Check if Raw card is selected
  const isRawCard = itemSpecifics.professional_grader === 'Raw (Ungraded)';
  
  // Force refresh user data on mount to get latest Stripe subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      if (isAuthenticated && refreshUser) {
        try {
          await refreshUser();
        } catch (error) {
          console.error('Failed to refresh subscription status:', error);
        }
      }
      setCheckingSubscription(false);
    };
    checkSubscription();
  }, [isAuthenticated, refreshUser]);

  // Check for prefilled data from AI Scanner
  useEffect(() => {
    if (location.state?.prefill) {
      const prefill = location.state.prefill;
      setFormData({
        player_name: prefill.player_name || '',
        sport: prefill.sport || '',
        set_name: prefill.set_name || '',
        year: prefill.year || new Date().getFullYear(),
        grade_company: prefill.grade_company || '',
        grade_value: prefill.grade_value || '',
        price: prefill.price || '',
        description: prefill.parallel ? `${prefill.parallel}${prefill.card_number ? ` - Card #${prefill.card_number}` : ''}` : ''
      });
      // Pre-fill item specifics from AI scan
      setItemSpecifics(prev => ({
        ...prev,
        card_number: prefill.card_number || '',
        parallel_variety: prefill.parallel || '',
        professional_grader: prefill.grade_company || '',
        grade: prefill.grade_value || ''
      }));
      if (prefill.image_url) {
        setImagePreview(prefill.image_url);
        setImages([prefill.image_url]);
      }
      setScanned(true);
      toast.success('Card data loaded from AI Scanner');
    }
  }, [location.state]);

  const sports = ['Basketball', 'Football', 'Baseball', 'Hockey', 'Soccer', 'Golf', 'Tennis', 'UFC/MMA', 'Wrestling', 'Racing', 'Pokemon TCG', 'Yu-Gi-Oh', 'Magic: The Gathering', 'Other TCG'];
  const gradeCompanies = ['PSA', 'BGS', 'SGC', 'CGC', 'Raw'];
  const professionalGraders = ['PSA', 'BGS', 'SGC', 'CGC', 'CSG', 'HGA', 'Raw (Ungraded)'];
  const rawConditions = ['Mint', 'NM-MT', 'NM', 'EX', 'VG', 'Poor'];
  const conditions = ['Mint', 'NM-MT', 'NM', 'EX', 'VG', 'Poor'];

  // Use centralized subscription check utility
  // Prioritizes Stripe subscription_status over local booleans
  const canSell = isAuthenticated && hasSellerAccess(user);
  
  // Show loading state while checking subscription
  if (checkingSubscription) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <div className="noise-overlay" />
        <Navbar />
        <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto">
          <div className="stat-card p-8 text-center">
            <RefreshCw className="w-16 h-16 text-[#00BFFF] mx-auto mb-6 animate-spin" />
            <h1 className="font-heading font-bold text-2xl text-white mb-4">
              Checking Subscription Status...
            </h1>
            <p className="text-zinc-400">
              Verifying your seller access
            </p>
          </div>
        </main>
      </div>
    );
  }
  
  if (isAuthenticated && !canSell) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <div className="noise-overlay" />
        <Navbar />
        
        <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto">
          <div className="stat-card p-8 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-6" />
            <h1 className="font-heading font-bold text-3xl uppercase text-white mb-4">
              Seller Subscription Required
            </h1>
            <p className="text-zinc-400 mb-8">
              Subscribe for $20/month to list unlimited cards with 0% seller fees.
            </p>
            <Button 
              onClick={() => navigate('/subscription')}
              className="btn-primary"
              data-testid="subscribe-cta"
            >
              Subscribe Now - $20/month
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Debounced search for set autocomplete
  const searchSets = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoadingSuggestions(true);
    try {
      const res = await api().get(`/cards/search-database?q=${encodeURIComponent(query)}&category=${formData.sport || ''}`);
      const combined = [...(res.data.panini || []), ...(res.data.topps || [])];
      // Dedupe by set_name + year
      const unique = [];
      const seen = new Set();
      for (const item of combined) {
        const key = `${item.set_name}-${item.year}`;
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(item);
        }
      }
      setSuggestions(unique.slice(0, 8));
    } catch (e) {
      console.error('Search error:', e);
    }
    setLoadingSuggestions(false);
  };

  const selectSuggestion = (item) => {
    setFormData(prev => ({
      ...prev,
      set_name: item.set_name,
      year: item.year?.split('-')[0] || prev.year,
      sport: item.category || prev.sport
    }));
    setSetSearch(item.set_name);
    setShowSuggestions(false);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
      setScanned(false);
    }
  };

  const handleScan = async () => {
    if (!image) {
      toast.error('Please upload an image first');
      return;
    }

    setScanning(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', image);
      
      const response = await api().post('/cards/scan', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const result = response.data;
      setFormData({
        ...formData,
        player_name: result.player_name || '',
        sport: result.sport || '',
        set_name: result.set_name || '',
        year: result.year || new Date().getFullYear(),
        grade_company: result.grade_company || '',
        grade_value: result.grade_value || ''
      });
      
      setScanned(true);
      toast.success(`AI recognized: ${result.player_name} (${Math.round(result.confidence * 100)}% confidence)`);
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('AI scan failed. Please fill in details manually.');
    } finally {
      setScanning(false);
    }
  };

  // Handle grader selection - update condition automatically
  const handleGraderChange = (value) => {
    const isRaw = value === 'Raw (Ungraded)';
    setItemSpecifics({ 
      ...itemSpecifics, 
      professional_grader: value,
      condition: isRaw ? 'Raw' : 'Graded',
      // Clear graded fields when switching to raw
      grade: isRaw ? '' : itemSpecifics.grade,
      cert_number: isRaw ? '' : itemSpecifics.cert_number,
      // Clear raw condition when switching to graded
      raw_condition: isRaw ? itemSpecifics.raw_condition : ''
    });
    // Also update legacy grade_company field
    setFormData({ 
      ...formData, 
      grade_company: isRaw ? 'Raw' : value,
      grade_value: isRaw ? '' : formData.grade_value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.player_name || !formData.sport || !formData.set_name || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!itemSpecifics.professional_grader) {
      toast.error('Please select a grading option');
      return;
    }

    if (parseFloat(formData.price) <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }

    // Validate raw condition if raw card
    if (isRawCard && !itemSpecifics.raw_condition) {
      toast.error('Please select a condition for your raw card');
      return;
    }

    // Validate grade if graded card
    if (!isRawCard && !itemSpecifics.grade) {
      toast.error('Please enter a grade for your graded card');
      return;
    }

    setSubmitting(true);
    try {
      // Build item_specifics object (only include non-empty values)
      const item_specifics = {};
      Object.entries(itemSpecifics).forEach(([key, value]) => {
        if (value !== '' && value !== false && value !== null && value !== undefined) {
          // Convert total_print_run to number if present
          if (key === 'total_print_run' && value) {
            item_specifics[key] = parseInt(value, 10);
          } else {
            item_specifics[key] = value;
          }
        }
      });

      const listingData = {
        ...formData,
        year: parseInt(formData.year),
        price: parseFloat(formData.price),
        image_url: images[0] || null,
        images: images,
        item_specifics: Object.keys(item_specifics).length > 0 ? item_specifics : null
      };

      await api().post('/cards', listingData);
      toast.success('Card listed successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle images change from MultiImageUpload
  const handleImagesChange = (newImages) => {
    setImages(newImages);
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-4xl uppercase text-white">List Your Card</h1>
            <p className="text-zinc-400 mt-2">Professional listing with detailed item specifics</p>
          </div>
          <Link to="/scanner">
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500" data-testid="ai-scanner-link">
              <Sparkles className="w-4 h-4 mr-2" />
              Advanced AI Scanner
            </Button>
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Top Section: Images + Basic Info */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Image Upload */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300 text-lg font-semibold">Card Photos</Label>
                <span className="text-xs text-zinc-500 flex items-center gap-1">
                  <Images className="w-3 h-3" />
                  Up to 5 images
                </span>
              </div>
              <MultiImageUpload
                maxImages={5}
                initialImages={images}
                onImagesChange={handleImagesChange}
              />
            </div>

            {/* Basic Card Info */}
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Star className="w-5 h-5 text-blue-400" />
                Card Basics
              </h2>
              
              <div>
                <Label className="text-zinc-300">Player/Character Name *</Label>
                <Input
                  value={formData.player_name}
                  onChange={(e) => setFormData({ ...formData, player_name: e.target.value })}
                  className="input-field mt-1"
                  placeholder="Michael Jordan, Charizard, etc."
                  required
                  data-testid="input-player"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Sport/Category *</Label>
                  <Select 
                    value={formData.sport} 
                    onValueChange={(v) => setFormData({ ...formData, sport: v })}
                  >
                    <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="select-sport">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-white/10">
                      {sports.map((sport) => (
                        <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-zinc-300">Year *</Label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    className="input-field mt-1"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    required
                    data-testid="input-year"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <Label className="text-zinc-300">Brand/Product *</Label>
                  <Input
                    value={setSearch || formData.set_name}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSetSearch(val);
                      setFormData({ ...formData, set_name: val });
                      setShowSuggestions(true);
                      searchSets(val);
                    }}
                    onFocus={() => setSearch && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="input-field mt-1"
                    placeholder="Prizm, Topps Chrome..."
                    required
                    data-testid="input-set"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-zinc-800 border border-zinc-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {suggestions.map((item, idx) => (
                        <div
                          key={idx}
                          className="px-3 py-2 hover:bg-zinc-700 cursor-pointer text-sm"
                          onMouseDown={() => selectSuggestion(item)}
                        >
                          <span className="text-white">{item.set_name}</span>
                          <span className="text-zinc-400 ml-2">{item.year}</span>
                          <span className="text-zinc-500 ml-2 text-xs">{item.brand}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {loadingSuggestions && (
                    <div className="absolute right-3 top-9 text-zinc-400 text-xs">Loading...</div>
                  )}
                </div>
                <div>
                  <Label className="text-zinc-300">Card #</Label>
                  <Input
                    value={itemSpecifics.card_number}
                    onChange={(e) => setItemSpecifics({ ...itemSpecifics, card_number: e.target.value })}
                    className="input-field mt-1"
                    placeholder="e.g., 220"
                    data-testid="input-card-number"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Grading / Condition Section */}
          <div className="stat-card p-6 space-y-5">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              {isRawCard ? (
                <Package className="w-5 h-5 text-orange-400" />
              ) : (
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
              )}
              {isRawCard ? 'Raw Card Condition' : 'Slab Details'}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-300">Card Type *</Label>
                <Select 
                  value={itemSpecifics.professional_grader} 
                  onValueChange={handleGraderChange}
                >
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="select-grader">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-white/10">
                    {professionalGraders.map((grader) => (
                      <SelectItem key={grader} value={grader}>
                        {grader === 'Raw (Ungraded)' ? (
                          <span className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-orange-400" />
                            {grader}
                          </span>
                        ) : grader}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Conditional: Show Grade & Cert for graded cards */}
              {!isRawCard && (
                <>
                  <div>
                    <Label className="text-zinc-300">Grade *</Label>
                    <Input
                      value={itemSpecifics.grade || formData.grade_value}
                      onChange={(e) => {
                        setItemSpecifics({ ...itemSpecifics, grade: e.target.value });
                        setFormData({ ...formData, grade_value: e.target.value });
                      }}
                      className="input-field mt-1"
                      placeholder="10, 9.5, Auto 10"
                      disabled={!itemSpecifics.professional_grader}
                      data-testid="input-grade"
                    />
                  </div>

                  <div>
                    <Label className="text-zinc-300">Certification #</Label>
                    <Input
                      value={itemSpecifics.cert_number}
                      onChange={(e) => setItemSpecifics({ ...itemSpecifics, cert_number: e.target.value })}
                      className="input-field mt-1"
                      placeholder="e.g., 12345678"
                      data-testid="input-cert-number"
                    />
                  </div>
                </>
              )}

              {/* Conditional: Show Raw Condition dropdown for raw cards */}
              {isRawCard && (
                <div className="md:col-span-2">
                  <Label className="text-zinc-300">Condition *</Label>
                  <Select 
                    value={itemSpecifics.raw_condition} 
                    onValueChange={(v) => setItemSpecifics({ ...itemSpecifics, raw_condition: v })}
                  >
                    <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="select-raw-condition">
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-white/10">
                      {rawConditions.map((condition) => (
                        <SelectItem key={condition} value={condition}>{condition}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">
                    Select the condition that best describes your ungraded card
                  </p>
                </div>
              )}
            </div>

            {/* Raw Card Info Banner */}
            {isRawCard && (
              <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                <p className="text-xs text-orange-400">
                  <span className="font-semibold">Raw Card:</span> This card has not been professionally graded. 
                  Buyers will see the condition you specify.
                </p>
              </div>
            )}
          </div>

          {/* High-Value Attributes Section */}
          <div className="stat-card p-6 space-y-5">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-400" />
              High-Value Attributes
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Autograph Toggle */}
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                  <div>
                    <Label className="text-zinc-200 font-medium">Autographed</Label>
                    <p className="text-xs text-zinc-500">Is this card signed?</p>
                  </div>
                  <Switch
                    checked={itemSpecifics.is_autographed}
                    onCheckedChange={(checked) => setItemSpecifics({ ...itemSpecifics, is_autographed: checked })}
                    data-testid="toggle-autograph"
                  />
                </div>
                
                {itemSpecifics.is_autographed && (
                  <div className="grid grid-cols-2 gap-3 pl-2 border-l-2 border-amber-500/30">
                    <div>
                      <Label className="text-zinc-400 text-sm">Signer Name</Label>
                      <Input
                        value={itemSpecifics.signer_name}
                        onChange={(e) => setItemSpecifics({ ...itemSpecifics, signer_name: e.target.value })}
                        className="input-field mt-1"
                        placeholder="e.g., Michael Jordan"
                        data-testid="input-signer"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-sm">Authentication</Label>
                      <Input
                        value={itemSpecifics.auto_authentication}
                        onChange={(e) => setItemSpecifics({ ...itemSpecifics, auto_authentication: e.target.value })}
                        className="input-field mt-1"
                        placeholder="Panini, Topps, JSA"
                        data-testid="input-auth"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Serial Numbered Toggle */}
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                  <div>
                    <Label className="text-zinc-200 font-medium">Serial Numbered</Label>
                    <p className="text-xs text-zinc-500">Is this card numbered? (e.g., /99)</p>
                  </div>
                  <Switch
                    checked={itemSpecifics.is_serial_numbered}
                    onCheckedChange={(checked) => setItemSpecifics({ ...itemSpecifics, is_serial_numbered: checked })}
                    data-testid="toggle-serial"
                  />
                </div>
                
                {itemSpecifics.is_serial_numbered && (
                  <div className="grid grid-cols-2 gap-3 pl-2 border-l-2 border-blue-500/30">
                    <div>
                      <Label className="text-zinc-400 text-sm">Serial #</Label>
                      <Input
                        value={itemSpecifics.serial_number}
                        onChange={(e) => setItemSpecifics({ ...itemSpecifics, serial_number: e.target.value })}
                        className="input-field mt-1"
                        placeholder="e.g., 01"
                        data-testid="input-serial"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-sm">Print Run</Label>
                      <Input
                        type="number"
                        value={itemSpecifics.total_print_run}
                        onChange={(e) => setItemSpecifics({ ...itemSpecifics, total_print_run: e.target.value })}
                        className="input-field mt-1"
                        placeholder="e.g., 99"
                        data-testid="input-print-run"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Parallel/Variety */}
              <div>
                <Label className="text-zinc-300">Parallel/Variety</Label>
                <Input
                  value={itemSpecifics.parallel_variety}
                  onChange={(e) => setItemSpecifics({ ...itemSpecifics, parallel_variety: e.target.value })}
                  className="input-field mt-1"
                  placeholder="Silver Prizm, Gold Refractor, Holo"
                  data-testid="input-parallel"
                />
              </div>

              {/* Rookie Card Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10 h-fit mt-6">
                <div className="flex items-center gap-3">
                  <div className="px-2 py-1 rounded bg-emerald-500/20 border border-emerald-500/30">
                    <span className="text-xs font-bold text-emerald-400">RC</span>
                  </div>
                  <div>
                    <Label className="text-zinc-200 font-medium">Rookie Card</Label>
                    <p className="text-xs text-zinc-500">Player's first-year card</p>
                  </div>
                </div>
                <Switch
                  checked={itemSpecifics.is_rookie_card}
                  onCheckedChange={(checked) => setItemSpecifics({ ...itemSpecifics, is_rookie_card: checked })}
                  data-testid="toggle-rookie"
                />
              </div>
            </div>
          </div>

          {/* Pricing & Description */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-5">
              <div>
                <Label className="text-zinc-300">Price (USD) *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="input-field pl-12 text-xl font-mono"
                    placeholder="0.00"
                    required
                    data-testid="input-price"
                  />
                </div>
                <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  You keep 100% - buyers pay the 3.5% fee
                </p>
              </div>
            </div>

            <div>
              <Label className="text-zinc-300">Description (optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-field mt-1 min-h-[120px] resize-none"
                placeholder="Add any additional details about the card, condition notes, or special features..."
                data-testid="input-description"
              />
            </div>
          </div>

          {/* Item Specifics Preview */}
          {(itemSpecifics.professional_grader || itemSpecifics.is_autographed || itemSpecifics.is_serial_numbered || itemSpecifics.is_rookie_card || itemSpecifics.parallel_variety) && (
            <div className="stat-card p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase mb-4">Item Specifics Preview</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {formData.year && (
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-zinc-500">Year</p>
                    <p className="text-sm font-medium text-white">{formData.year}</p>
                  </div>
                )}
                {formData.set_name && (
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-zinc-500">Brand/Product</p>
                    <p className="text-sm font-medium text-white">{formData.set_name}</p>
                  </div>
                )}
                {itemSpecifics.card_number && (
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-zinc-500">Card #</p>
                    <p className="text-sm font-medium text-white">#{itemSpecifics.card_number}</p>
                  </div>
                )}
                {itemSpecifics.professional_grader && (
                  <div className={`p-3 rounded-lg ${isRawCard ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-white/5'}`}>
                    <p className="text-xs text-zinc-500">{isRawCard ? 'Card Type' : 'Grader'}</p>
                    <p className={`text-sm font-medium ${isRawCard ? 'text-orange-400' : 'text-white'}`}>
                      {itemSpecifics.professional_grader}
                    </p>
                  </div>
                )}
                {!isRawCard && (itemSpecifics.grade || formData.grade_value) && (
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-zinc-500">Grade</p>
                    <p className="text-sm font-medium text-white">{itemSpecifics.grade || formData.grade_value}</p>
                  </div>
                )}
                {!isRawCard && itemSpecifics.cert_number && (
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-zinc-500">Cert #</p>
                    <p className="text-sm font-medium text-white">{itemSpecifics.cert_number}</p>
                  </div>
                )}
                {isRawCard && itemSpecifics.raw_condition && (
                  <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                    <p className="text-xs text-orange-400">Condition</p>
                    <p className="text-sm font-medium text-white">{itemSpecifics.raw_condition}</p>
                  </div>
                )}
                {itemSpecifics.is_autographed && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-xs text-amber-400">Autographed</p>
                    <p className="text-sm font-medium text-white">{itemSpecifics.signer_name || 'Yes'}</p>
                  </div>
                )}
                {itemSpecifics.is_serial_numbered && (
                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-xs text-blue-400">Serial #</p>
                    <p className="text-sm font-medium text-white">
                      {itemSpecifics.serial_number || '??'}/{itemSpecifics.total_print_run || '??'}
                    </p>
                  </div>
                )}
                {itemSpecifics.parallel_variety && (
                  <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <p className="text-xs text-purple-400">Parallel</p>
                    <p className="text-sm font-medium text-white">{itemSpecifics.parallel_variety}</p>
                  </div>
                )}
                {itemSpecifics.is_rookie_card && (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-xs text-emerald-400">Rookie Card</p>
                    <p className="text-sm font-bold text-emerald-400">RC</p>
                  </div>
                )}
                <div className={`p-3 rounded-lg ${isRawCard ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-white/5'}`}>
                  <p className="text-xs text-zinc-500">Type</p>
                  <p className={`text-sm font-medium ${isRawCard ? 'text-orange-400' : 'text-white'}`}>
                    {isRawCard ? 'Raw' : 'Graded'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full btn-primary h-14 text-lg"
            data-testid="submit-listing"
          >
            {submitting ? <LoadingSpinner size="sm" /> : 'List Card for Sale'}
          </Button>
        </form>
      </main>
    </div>
  );
};

export default SellPage;
